import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import SEO from "../components/SEO";
import HowWeWork from "../components/HowWeWork";
import { FadeUp } from "../components/Motion";
import { ArrowUpRight } from "lucide-react";
import { CONTACT, LICENSES } from "../lib/content";

const practiceCards = [
  {
    label: "Primary Practice",
    title: "Construction",
    text: "Custom homes, whole-home renovations, kitchens and baths, and commercial build-outs. You work directly with the people managing your project.",
    linkTo: "/custom-homes",
    linkLabel: "Explore Construction",
    featured: true,
  },
  {
    label: null,
    title: "Disaster Recovery",
    text: "FEMA Public Assistance, cost estimating and validation, and recovery program management for public agencies, nonprofits and the firms that support them.",
    linkTo: "/disaster-recovery",
    linkLabel: "Explore Disaster Recovery",
  },
  {
    label: "Specialized Practice",
    title: "Expert Witness",
    text: "Independent construction analysis and testimony from Col. (Ret.) Bob Carpenter, P.E., former Commander of the U.S. Army Corps of Engineers Jacksonville District.",
    linkTo: "/expert-witness",
    linkLabel: "Explore Expert Witness",
  },
];

const trackRecord = [
  { n: "2006", t: "Founded in Tampa" },
  { n: "$18M+", t: "Federal construction contracts with the Corps of Engineers and the VA" },
  { n: "150+", t: "FEMA Public Assistance grants managed" },
  { n: "20+", t: "Retail build-outs delivered across Florida" },
];

const audiences = [
  { h: "Homeowners", d: "Custom homes, renovations and remodels across Tampa Bay.", to: "/custom-homes" },
  { h: "Architects & designers", d: "A builder who protects your design and solves problems on paper first.", to: "/work-with-us#designers" },
  { h: "Builders, trades & vendors", d: "Clear scopes, organized sites and steady communication.", to: "/work-with-us#trade-partners" },
  { h: "Public agencies & nonprofits", d: "FEMA Public Assistance and disaster recovery support.", to: "/disaster-recovery" },
  { h: "Attorneys", d: "Independent expert analysis and testimony.", to: "/expert-witness" },
];

const selectedWork = [
  { slug: "waverly-park-kitchen", title: "Waverly Park Kitchen", loc: "South Tampa", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/gfxrb2yy_After%201.jpeg" },
  { slug: "north-tampa-custom-home", title: "North Tampa Custom Home", loc: "North Tampa", pos: "object-[center_60%]", img: "/images/projects/bcpeabody-north-tampa-custom-home-primary-bath-07.jpg" },
  { slug: "goddard-school-kennedy-tampa", title: "Goddard School", loc: "Kennedy Boulevard, Tampa", img: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/ci6ibh3r_image.png" },
];

const leadership = [
  {
    name: "Col. (Ret.) Bob Carpenter, P.E.",
    role: "Founder, President & CEO",
    bio: "Former Commander of the U.S. Army Corps of Engineers Jacksonville District. West Point graduate and Stanford-trained civil engineer.",
    photo: "/images/team/bob-carpenter.jpg",
  },
  {
    name: "Kimberly Carpenter",
    role: "Operations",
    bio: "13+ years in construction and disaster recovery. Your main point of contact.",
    photo: "https://customer-assets.emergentagent.com/job_company-site-new/artifacts/qxlydxdj_image.png",
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Tampa General Contractor | Custom Homes"
        description="Family-owned, engineer-led Tampa general contractor since 2006. Custom homes, renovations, kitchens and baths, commercial and federal construction."
        path="/"
      />
      <Hero />

      {/* 2. Trust bar */}
      <section data-testid="home-trust-bar" className="bg-cream border-b border-ink/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 md:py-7">
          <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 gap-y-3 text-[12px] tracking-[0.14em] uppercase text-ink/85">
            <span className="flex items-center gap-2"><span className="w-1 h-1 bg-copper rounded-full" /> Family-owned since 2006</span>
            <span className="flex items-center gap-2"><span className="w-1 h-1 bg-copper rounded-full" /> Engineer-led</span>
            <span className="flex items-center gap-2"><span className="w-1 h-1 bg-copper rounded-full" /> Service-Disabled Veteran-Owned</span>
            <span className="flex items-center gap-2"><span className="w-1 h-1 bg-copper rounded-full" /> Licensed & Insured, CGC1515086</span>
          </div>
        </div>
      </section>

      {/* 3. What we do */}
      <section data-testid="home-what-we-do" className="py-20 md:py-24" style={{ backgroundColor: "#F7F2E9" }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-12">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4" style={{ color: "#B97842" }}>What We Do</div>
            <h2 className="font-serif font-medium tracking-tight" style={{ fontSize: "clamp(30px, 4vw, 44px)", color: "#302B26", lineHeight: 1.15 }}>What we do</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {practiceCards.map((c) => (
              <article key={c.title} data-testid={`practice-${c.title.toLowerCase().replace(/ /g,'-')}`} className="border-t-2 pt-6 flex flex-col" style={{ borderColor: c.featured ? "#B97842" : "rgba(48,43,38,0.22)" }}>
                {c.label && <div className="text-[11px] tracking-[0.16em] uppercase mb-3" style={{ color: c.featured ? "#B97842" : "rgba(48,43,38,0.55)" }}>{c.label}</div>}
                <h3 className="font-serif font-medium tracking-tight mb-4" style={{ fontSize: 26, color: "#302B26", lineHeight: 1.2 }}>{c.title}</h3>
                <p className="leading-relaxed mb-5 flex-1" style={{ fontSize: 16, color: "rgba(48,43,38,0.78)" }}>{c.text}</p>
                <Link to={c.linkTo} className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium hover:opacity-80" style={{ color: "#B97842" }}>
                  {c.linkLabel} <ArrowUpRight size={14} />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Track record */}
      <section data-testid="home-track-record" className="py-16 md:py-20 bg-cream-light">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
          {trackRecord.map((s) => (
            <div key={s.n} className="border-l-2 pl-4" style={{ borderColor: "#B97842" }}>
              <div className="font-serif text-5xl md:text-6xl font-medium text-ink leading-none mb-3">{s.n}</div>
              <div className="text-sm text-ink/70 leading-snug">{s.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Who we work with */}
      <section data-testid="home-who-we-work-with" className="py-20 md:py-24 bg-cream">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-10">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4" style={{ color: "#B97842" }}>Who We Work With</div>
            <h2 className="font-serif font-medium tracking-tight" style={{ fontSize: "clamp(30px, 4vw, 44px)", color: "#302B26", lineHeight: 1.15 }}>Who we work with</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-ink/10">
            {audiences.map((a) => (
              <Link key={a.h} to={a.to} className="bg-cream p-6 border-t-2 border-copper hover:bg-cream-light transition-colors group">
                <h3 className="font-serif text-xl text-ink mb-2 tracking-tight">{a.h}</h3>
                <p className="text-sm text-ink/70 leading-relaxed mb-3">{a.d}</p>
                <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-copper group-hover:gap-2 transition-all">
                  Learn more <ArrowUpRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Selected work */}
      <HowWeWork />
      <section data-testid="home-selected-work" className="py-20 md:py-24 bg-cream-light">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-12">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4" style={{ color: "#B97842" }}>Selected Work</div>
            <h2 className="font-serif font-medium tracking-tight" style={{ fontSize: "clamp(30px, 4vw, 44px)", color: "#302B26", lineHeight: 1.15 }}>Selected work</h2>
          </div>          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {selectedWork.map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`home-selected-${p.slug}`} className="group block">
                <div className="relative h-[320px] overflow-hidden bg-ink mb-4">
                  <img src={p.img} alt={`${p.title}, ${p.loc}`} loading="lazy" width="800" height="640" className={`w-full h-full object-cover ${p.pos || ""} group-hover:scale-105 transition-transform duration-[1200ms]`} />
                </div>
                <h3 className="font-serif text-2xl text-ink tracking-tight mb-1">{p.title}</h3>
                <p className="text-sm text-ink/60">{p.loc}</p>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/projects" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-copper hover:text-copper-deep">
              See all work <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. Leadership */}
      <section data-testid="home-leadership" className="py-20 md:py-24 bg-cream">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-12">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4" style={{ color: "#B97842" }}>Leadership</div>
            <h2 className="font-serif font-medium tracking-tight" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", color: "#302B26", lineHeight: 1.15 }}>Experienced people. Practical leadership.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {leadership.map((m) => (
              <FadeUp key={m.name}>
                <article>
                  <div className="relative h-[420px] overflow-hidden bg-ink mb-6">
                    <img src={m.photo} alt={m.name} loading="lazy" width="800" height="900" className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-serif text-3xl text-ink tracking-tight mb-2">{m.name}</h3>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-copper mb-4">{m.role}</div>
                  <p className="text-ink/75 leading-relaxed">{m.bio}</p>
                </article>
              </FadeUp>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/team" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-copper hover:text-copper-deep">
              Meet the team <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 8. Where we're going */}
      <section data-testid="home-whats-next" className="py-20 md:py-24 bg-cream-light">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4" style={{ color: "#B97842" }}>What's Next</div>
            <h2 className="font-serif font-medium tracking-tight" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", color: "#302B26", lineHeight: 1.15 }}>The next twenty years.</h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="text-ink/80 leading-relaxed" style={{ fontSize: 17 }}>
            BCPeabody started with federal and civil work and grew into homes, commercial build-outs and disaster recovery. Today Bob is joined by his daughter, Kimberly, and the firm is focused on custom homes, renovations, additions and design-build projects across Tampa Bay, backed by the same discipline that delivered work for the Corps of Engineers and the VA.
            </p>
          </div>
        </div>
      </section>

      {/* 9. CTA band */}
      <section data-testid="home-cta-band" className="py-20 md:py-24" style={{ backgroundColor: "#2E2923" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center" style={{ color: "#F7F2E9" }}>
          <h2 className="font-serif font-medium tracking-tight mb-6" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", lineHeight: 1.2 }}>
            Planning a project?
          </h2>
          <p className="leading-relaxed mb-10 mx-auto" style={{ fontSize: 17, color: "rgba(247,242,233,0.82)", maxWidth: 620 }}>
            Tell us what you have in mind. We respond within one business day.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/contact" data-testid="home-cta-start" className="btn-copper">Start a Project</Link>
            <a href={CONTACT.phoneHref} data-testid="home-cta-call" className="text-[13px] tracking-[0.14em] uppercase link-underline text-cream-light">Call {CONTACT.phone} →</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
